import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import type { BlogAuthorDto } from '../../../shared/models/blog.models';
import { BlogsService } from '../../../shared/services/blogs.service';
import {
  AdminButtonComponent,
  AdminCardComponent,
  AdminConfirmDialogService,
  AdminEmptyStateComponent,
  AdminModalComponent,
  AdminPageHeaderComponent,
  AdminToastService,
} from '../../../shared/admin';
import { PermissionService } from '../../permissions/permission.service';

@Component({
  selector: 'app-platform-blog-authors-page',
  standalone: true,
  imports: [FormsModule, AdminPageHeaderComponent, AdminCardComponent, AdminButtonComponent, AdminModalComponent, AdminEmptyStateComponent],
  template: `
    <section>
      <app-admin-page-header title="Blog authors">
        <ng-container actions>
          @if (showCreate()) {
            <app-admin-button variant="primary" type="button" (click)="openCreate()" [disabled]="!canCreate()">New author</app-admin-button>
          }
        </ng-container>
      </app-admin-page-header>
      <div class="cg-admin-grid cg-admin-grid--2">
        @for (item of items(); track item._id) {
          <app-admin-card>
            <div class="font-medium">{{ item.displayName }}</div>
            @if (item.role) {
              <p class="cg-form-help">{{ item.role }}</p>
            }
            <div class="mt-3 flex gap-2">
              @if (showEdit()) {
                <app-admin-button variant="ghost" type="button" (click)="edit(item)" [disabled]="!canEdit()">Edit</app-admin-button>
              }
              @if (showDelete()) {
                <app-admin-button variant="danger" type="button" (click)="remove(item)" [disabled]="!canDelete()">Delete</app-admin-button>
              }
            </div>
          </app-admin-card>
        }
        @if (items().length === 0) {
          <app-admin-empty-state icon="user" title="No authors yet" />
        }
      </div>
      @if (showForm()) {
        <app-admin-modal [title]="editingId() ? 'Edit author' : 'New author'" (closed)="closeForm()">
          <label class="block text-sm">
            <span>Display name</span>
            <input class="mt-1 w-full rounded border p-2" [(ngModel)]="form.displayName" name="displayName" />
          </label>
          <label class="block text-sm mt-3">
            <span>Role</span>
            <input class="mt-1 w-full rounded border p-2" [(ngModel)]="form.role" name="role" />
          </label>
          <label class="block text-sm mt-3">
            <span>Bio</span>
            <textarea class="mt-1 w-full rounded border p-2" rows="3" [(ngModel)]="form.bio" name="bio"></textarea>
          </label>
          <div class="mt-4">
            <app-admin-button variant="primary" type="button" (click)="save()">Save</app-admin-button>
          </div>
        </app-admin-modal>
      }
    </section>
  `,
})
export class PlatformBlogAuthorsPageComponent implements OnInit {
  private readonly blogs = inject(BlogsService);
  private readonly confirmDialog = inject(AdminConfirmDialogService);
  private readonly toast = inject(AdminToastService);
  private readonly permissions = inject(PermissionService);

  readonly showCreate = computed(() => this.permissions.canShow('website.blogs', 'create'));
  readonly canCreate = computed(() => this.permissions.canExecute('website.blogs', 'create'));
  readonly showEdit = computed(() => this.permissions.canShow('website.blogs', 'edit'));
  readonly canEdit = computed(() => this.permissions.canExecute('website.blogs', 'edit'));
  readonly showDelete = computed(() => this.permissions.canShow('website.blogs', 'delete'));
  readonly canDelete = computed(() => this.permissions.canExecute('website.blogs', 'delete'));

  readonly items = signal<BlogAuthorDto[]>([]);
  readonly showForm = signal(false);
  readonly editingId = signal<string | null>(null);
  form = { displayName: '', role: '', bio: '' };

  ngOnInit(): void {
    this.reload();
  }

  reload(): void {
    this.blogs.listAuthors().subscribe((items) => this.items.set(items));
  }

  openCreate(): void {
    this.editingId.set(null);
    this.form = { displayName: '', role: '', bio: '' };
    this.showForm.set(true);
  }

  edit(item: BlogAuthorDto): void {
    this.editingId.set(item._id);
    this.form = { displayName: item.displayName, role: item.role ?? '', bio: item.bio ?? '' };
    this.showForm.set(true);
  }

  closeForm(): void {
    this.showForm.set(false);
  }

  save(): void {
    const id = this.editingId();
    const req = id ? this.blogs.updateAuthor(id, this.form) : this.blogs.createAuthor(this.form);
    req.subscribe({
      next: () => {
        this.toast.success(id ? 'Author updated.' : 'Author created.');
        this.closeForm();
        this.reload();
      },
      error: (err) => this.toast.error(err?.error?.message ?? 'Could not save author.'),
    });
  }

  async remove(item: BlogAuthorDto): Promise<void> {
    const confirmed = await this.confirmDialog.confirm({
      title: 'Delete author',
      message: `Delete "${item.displayName}"?`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      confirmVariant: 'danger',
    });
    if (!confirmed) return;

    this.blogs.removeAuthor(item._id).subscribe({
      next: () => {
        this.toast.success(`"${item.displayName}" deleted.`);
        this.reload();
      },
      error: (err) => this.toast.error(err?.error?.message ?? 'Could not delete author.'),
    });
  }
}
